import React, { useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Typography,
  Box,
  Button,
  Chip,
  Stack,
  Divider,
  IconButton,
  Tooltip,
  CircularProgress
} from '@mui/material';
import {
  AutoAwesome as AutoAwesomeIcon,
  Close as CloseIcon,
  Add as AddIcon,
  Edit as EditIcon
} from '@mui/icons-material';

const PERIOD_LABELS = {
  weekly: '/week',
  monthly: '/month',
  quarterly: '/quarter',
  yearly: '/year'
};

const recommendationKey = (rec) =>
  rec.action === 'modify' ? `modify|${rec.budgetId}` : `create|${rec.categoryId}`;

const AiBudgetRecommendationsDialog = ({
  recommendations,
  onApply,
  onDismiss,
  onClose,
  formatCurrency,
  getCategoryColor
}) => {
  const [applyingKey, setApplyingKey] = useState(null);

  if (!recommendations) return null;

  const handleApply = async (rec) => {
    setApplyingKey(recommendationKey(rec));
    await onApply(rec);
    setApplyingKey(null);
  };

  return (
    <Dialog open fullWidth maxWidth="md" onClose={onClose}>
      <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1, pr: 6 }}>
        <AutoAwesomeIcon color="secondary" />
        Budget Recommendations
        <IconButton onClick={onClose} sx={{ position: 'absolute', right: 8, top: 8 }}>
          <CloseIcon />
        </IconButton>
      </DialogTitle>
      <DialogContent dividers>
        {recommendations.length === 0 ? (
          <Typography color="text.secondary" sx={{ py: 2 }}>
            Nice work! You've handled every recommendation. Your budgets are in great shape! 🎉
          </Typography>
        ) : (
          <>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              Based on your budgets and the last 6 months of category activity.
              Nothing changes until you apply a recommendation.
            </Typography>
            <Stack divider={<Divider />} spacing={0}>
              {recommendations.map((rec) => {
                const key = recommendationKey(rec);
                const isApplying = applyingKey === key;
                const periodLabel = rec.recurringPeriod ? PERIOD_LABELS[rec.recurringPeriod] || '' : '';
                return (
                  <Box key={key} sx={{ py: 2 }}>
                    <Box
                      sx={{
                        display: 'flex',
                        flexDirection: { xs: 'column', sm: 'row' },
                        justifyContent: 'space-between',
                        alignItems: { xs: 'stretch', sm: 'flex-start' },
                        gap: { xs: 1.5, sm: 2 }
                      }}
                    >
                      <Box sx={{ minWidth: 0, flex: 1 }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap', mb: 0.5 }}>
                          <Chip
                            label={rec.action === 'modify' ? 'Adjust budget' : 'New budget'}
                            size="small"
                            color={rec.action === 'modify' ? 'primary' : 'secondary'}
                            sx={{ fontSize: '0.7rem', height: 20, fontWeight: 700 }}
                          />
                          <Typography variant="subtitle2" fontWeight={700} noWrap>
                            {rec.action === 'modify' ? rec.budgetName : rec.categoryName}
                          </Typography>
                        </Box>

                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, flexWrap: 'wrap', mb: 1 }}>
                          <Chip
                            label={rec.categoryName}
                            size="small"
                            sx={{
                              fontSize: '0.75rem',
                              height: 22,
                              bgcolor: getCategoryColor ? `${getCategoryColor(rec.categoryId)}22` : undefined
                            }}
                          />
                          {rec.action === 'modify' ? (
                            <>
                              <Chip
                                label={`${formatCurrency(rec.currentAmount)}${periodLabel}`}
                                size="small"
                                variant="outlined"
                                sx={{ fontSize: '0.75rem', height: 22, textDecoration: 'line-through', color: 'text.secondary' }}
                              />
                              <Typography variant="body2" color="text.secondary">→</Typography>
                              <Chip
                                label={`${formatCurrency(rec.suggestedAmount)}${periodLabel}`}
                                size="small"
                                color="secondary"
                                variant="outlined"
                                sx={{ fontSize: '0.75rem', height: 22, fontWeight: 700 }}
                              />
                            </>
                          ) : (
                            <Chip
                              label={`${rec.type} · ${formatCurrency(rec.suggestedAmount)}${periodLabel}`}
                              size="small"
                              color="secondary"
                              variant="outlined"
                              sx={{ fontSize: '0.75rem', height: 22, fontWeight: 700 }}
                            />
                          )}
                        </Box>

                        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', fontStyle: 'italic' }}>
                          {rec.rationale}
                        </Typography>
                      </Box>

                      <Box
                        sx={{
                          display: 'flex',
                          alignItems: 'center',
                          gap: 0.5,
                          flexShrink: 0,
                          alignSelf: { xs: 'stretch', sm: 'flex-start' }
                        }}
                      >
                        <Button
                          variant="contained"
                          color="secondary"
                          size="small"
                          disabled={applyingKey !== null}
                          startIcon={
                            isApplying
                              ? <CircularProgress size={14} color="inherit" />
                              : rec.action === 'modify'
                                ? <EditIcon fontSize="small" />
                                : <AddIcon fontSize="small" />
                          }
                          onClick={() => handleApply(rec)}
                          sx={{
                            textTransform: 'none',
                            borderRadius: 2,
                            fontWeight: 600,
                            flex: { xs: 1, sm: 'none' }
                          }}
                        >
                          {isApplying
                            ? 'Applying...'
                            : rec.action === 'modify' ? 'Update budget' : 'Create budget'}
                        </Button>
                        <Tooltip title="Dismiss">
                          <IconButton
                            size="small"
                            onClick={() => onDismiss(rec)}
                            disabled={applyingKey !== null}
                            sx={{ color: 'text.secondary' }}
                          >
                            <CloseIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      </Box>
                    </Box>
                  </Box>
                );
              })}
            </Stack>
          </>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} sx={{ textTransform: 'none' }}>
          {recommendations.length === 0 ? 'Close' : 'Done'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default AiBudgetRecommendationsDialog;
